import React, { useState, useEffect } from 'react';
import { getUserStatistics, getRecentActivity, getSystemStats, verifyAdmin } from '../../services/adminService';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function AdminDashboard({ onNavigate }) {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalWalletBalance: 0,
    totalCommission: 0,
    totalFrozen: 0,
    totalBalance: 0,
    vipDistribution: { VIP1: 0, VIP2: 0, VIP3: 0, VIP4: 0 },
    vipPercentages: { VIP1: 0, VIP2: 0, VIP3: 0, VIP4: 0 },
    averageWalletBalance: 0,
    averageCommission: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAdmin } = useAdminAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setError('');
      
      // Verify admin access
      const adminStatus = await verifyAdmin();
      if (!adminStatus.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }
      
      // Load all data in parallel
      const [userStats, activity, system] = await Promise.all([
        getUserStatistics(),
        getRecentActivity(10),
        getSystemStats()
      ]);
      
      setStats(userStats);
      setRecentActivity(activity);
      setSystemStats(system);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error.message || 'Failed to load dashboard statistics');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getActivityIcon = (action) => {
    const iconMap = {
      'updateUser': 'bi-pencil-square',
      'deleteUser': 'bi-trash',
      'viewUser': 'bi-eye',
      'fetchAllUsers': 'bi-people',
      'viewActivityLogs': 'bi-clock-history',
      'createUser': 'bi-person-plus'
    };
    return iconMap[action] || 'bi-circle';
  };

  const getActivityColor = (action) => {
    const colorMap = {
      'updateUser': '#0dcaf0',
      'deleteUser': '#dc3545',
      'viewUser': '#198754',
      'fetchAllUsers': '#0d6efd',
      'viewActivityLogs': '#6f42c1',
      'createUser': '#20c997'
    };
    return colorMap[action] || '#6c757d';
  };

  const vipLevelConfig = {
    VIP1: { color: '#ffc107', bgGradient: 'linear-gradient(135deg, #ffc107 0%, #ffb300 100%)', icon: 'bi-star' },
    VIP2: { color: '#0dcaf0', bgGradient: 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)', icon: 'bi-star-fill' },
    VIP3: { color: '#dc3545', bgGradient: 'linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%)', icon: 'bi-award' },
    VIP4: { color: '#212529', bgGradient: 'linear-gradient(135deg, #212529 0%, #000000 100%)', icon: 'bi-trophy' }
  };

  // Main Statistics Cards
  const mainStatCards = [
    {
      title: 'Total Users',
      value: formatNumber(stats.totalUsers),
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderColor: '#667eea',
      description: 'Registered users'
    },
    {
      title: 'Active Users',
      value: formatNumber(stats.activeUsers),
      icon: 'bi-person-check-fill',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      borderColor: '#f5576c',
      description: 'Currently active'
    },
    {
      title: 'Total Wallet',
      value: formatCurrency(stats.totalWalletBalance),
      icon: 'bi-wallet2',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      borderColor: '#4facfe',
      description: 'Combined balance'
    },
    {
      title: 'Total Commission',
      value: formatCurrency(stats.totalCommission),
      icon: 'bi-cash-stack',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      borderColor: '#43e97b',
      description: 'Total earnings'
    }
  ];

  // Show error if unauthorized
  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header with Gradient - Responsive */}
      <div 
        className="mb-3 mb-md-4 p-3 p-md-4 rounded-3 text-white"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-column flex-md-row gap-3">
          <div>
            <h2 className="mb-1" style={{ fontWeight: '600', fontSize: isMobile ? '1.25rem' : '1.75rem' }}>
              <i className="bi bi-speedometer2 me-2"></i>
              <span className="d-none d-sm-inline">Dashboard Overview</span>
              <span className="d-sm-none">Dashboard</span>
            </h2>
            <p className="mb-0 opacity-75" style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>
              Comprehensive insights and analytics
            </p>
          </div>
          {systemStats && (
            <div className="text-end">
              <div className="badge bg-light text-dark px-2 px-md-3 py-2" style={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                <i className="bi bi-circle-fill text-success me-2" style={{ fontSize: '0.5rem' }}></i>
                System Online
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Main Statistics Cards - Responsive Grid */}
      <div className="row g-3 g-md-4 mb-3 mb-md-4">
        {mainStatCards.map((card, index) => (
          <div key={index} className="col-6 col-md-6 col-lg-3">
            <div 
              className="card h-100 border-0 shadow-sm"
              style={{
                borderLeft: `4px solid ${card.borderColor}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
              }}
            >
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '50px',
                      height: '50px',
                      background: card.gradient,
                      color: 'white'
                    }}
                  >
                    <i className={`bi ${card.icon}`} style={{ fontSize: '1.5rem' }}></i>
                  </div>
                </div>
                <h3 className="mb-1" style={{ fontWeight: '700', color: '#212529' }}>
                  {card.value}
                </h3>
                <h6 className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  {card.title}
                </h6>
                <small className="text-muted">{card.description}</small>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 g-md-4">
        {/* VIP Distribution Section */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div 
              className="card-header border-0 text-white"
              style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                borderRadius: '0.375rem 0.375rem 0 0'
              }}
            >
              <h5 className="mb-0">
                <i className="bi bi-bar-chart-fill me-2"></i>
                VIP Distribution
              </h5>
            </div>
            <div className="card-body">
              {Object.entries(vipLevelConfig).map(([level, config]) => {
                const count = stats.vipDistribution[level] || 0;
                const percentage = stats.vipPercentages[level] || 0;
                const totalVip = Object.values(stats.vipDistribution).reduce((a, b) => a + b, 0);
                
                return (
                  <div key={level} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <i className={`bi ${config.icon} me-2`} style={{ color: config.color, fontSize: '1.2rem' }}></i>
                        <span className="fw-semibold">{level}</span>
                        <span className="badge bg-light text-dark ms-2">{count} users</span>
                      </div>
                      <span className="text-muted">{percentage.toFixed(1)}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px', borderRadius: '10px', backgroundColor: '#e9ecef' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${percentage}%`,
                          background: config.bgGradient,
                          borderRadius: '10px',
                          transition: 'width 0.6s ease'
                        }}
                        aria-valuenow={percentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                );
              })}
              <div className="mt-3 pt-3 border-top">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Total VIP Users</span>
                  <span className="fw-bold">
                    {Object.values(stats.vipDistribution).reduce((a, b) => a + b, 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div 
              className="card-header border-0 text-white d-flex justify-content-between align-items-center"
              style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                borderRadius: '0.375rem 0.375rem 0 0'
              }}
            >
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Recent Activity
              </h5>
              {onNavigate && (
                <button 
                  className="btn btn-sm btn-light"
                  onClick={() => onNavigate('activity')}
                >
                  View All
                </button>
              )}
            </div>
            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {recentActivity.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2 mb-0">No recent activity</p>
                </div>
              ) : (
                <div className="timeline">
                  {recentActivity.map((activity, index) => (
                    <div 
                      key={activity.id || index}
                      className="d-flex mb-3 pb-3"
                      style={{ borderBottom: index < recentActivity.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: getActivityColor(activity.action) + '20',
                          color: getActivityColor(activity.action),
                          flexShrink: 0
                        }}
                      >
                        <i className={`bi ${getActivityIcon(activity.action)}`}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>
                              {activity.action.replace(/([A-Z])/g, ' $1').trim()}
                            </h6>
                            <p className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                              {activity.adminEmail}
                              {activity.targetUserEmail && activity.targetUserEmail !== 'N/A' && (
                                <span> → {activity.targetUserEmail}</span>
                              )}
                            </p>
                            {activity.reason && activity.reason !== 'No reason provided' && (
                              <small className="text-muted">{activity.reason}</small>
                            )}
                          </div>
                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {formatTimestamp(activity.timestamp)}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Panel - Responsive */}
      <div className="row g-3 g-md-4 mt-2">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div 
              className="card-header border-0 text-white"
              style={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                borderRadius: '0.375rem 0.375rem 0 0',
                padding: isMobile ? '0.75rem 1rem' : '1rem'
              }}
            >
              <h5 className="mb-0" style={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                <i className="bi bi-lightning-charge-fill me-2"></i>
                Quick Actions
              </h5>
            </div>
            <div className="card-body p-2 p-md-3">
              <div className="row g-2 g-md-3">
                <div className="col-6 col-md-3">
                  <div 
                    className="card border h-100 text-center p-2 p-md-3"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#f8f9fa',
                      minHeight: isMobile ? '120px' : 'auto',
                      touchAction: 'manipulation'
                    }}
                    onClick={() => onNavigate && onNavigate('users')}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                  >
                    <i className="bi bi-people-fill text-primary" style={{ fontSize: isMobile ? '2rem' : '2.5rem' }}></i>
                    <h6 className="mt-2 mt-md-3 mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>Manage Users</h6>
                    <small className="text-muted" style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>View and edit users</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div 
                    className="card border h-100 text-center p-2 p-md-3"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#f8f9fa',
                      minHeight: isMobile ? '120px' : 'auto',
                      touchAction: 'manipulation'
                    }}
                    onClick={() => onNavigate && onNavigate('activity')}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                  >
                    <i className="bi bi-clock-history text-info" style={{ fontSize: isMobile ? '2rem' : '2.5rem' }}></i>
                    <h6 className="mt-2 mt-md-3 mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>Activity Logs</h6>
                    <small className="text-muted" style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>System activities</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div 
                    className="card border h-100 text-center p-2 p-md-3"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#f8f9fa',
                      minHeight: isMobile ? '120px' : 'auto',
                      touchAction: 'manipulation'
                    }}
                    onClick={loadDashboardData}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise text-success" style={{ fontSize: isMobile ? '2rem' : '2.5rem' }}></i>
                    <h6 className="mt-2 mt-md-3 mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>Refresh Data</h6>
                    <small className="text-muted" style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Update statistics</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div 
                    className="card border h-100 text-center p-2 p-md-3"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#f8f9fa',
                      minHeight: isMobile ? '120px' : 'auto',
                      touchAction: 'manipulation'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                  >
                    <i className="bi bi-graph-up-arrow text-warning" style={{ fontSize: isMobile ? '2rem' : '2.5rem' }}></i>
                    <h6 className="mt-2 mt-md-3 mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>Analytics</h6>
                    <small className="text-muted" style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Reports & charts</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Statistics - Responsive */}
      <div className="row g-3 g-md-4 mt-2">
        <div className="col-6 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <i className="bi bi-currency-dollar text-success" style={{ fontSize: '2rem' }}></i>
              <h5 className="mt-2 mb-1">{formatCurrency(stats.averageWalletBalance)}</h5>
              <small className="text-muted">Average Wallet Balance</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <i className="bi bi-percent text-info" style={{ fontSize: '2rem' }}></i>
              <h5 className="mt-2 mb-1">{formatCurrency(stats.averageCommission)}</h5>
              <small className="text-muted">Average Commission</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <i className="bi bi-person-x text-danger" style={{ fontSize: '2rem' }}></i>
              <h5 className="mt-2 mb-1">{formatNumber(stats.inactiveUsers)}</h5>
              <small className="text-muted">Inactive Users</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
